import {
  DOUBLE_ROOM_COMBINATIONS,
  INDIVIDUAL_ROOM_COMBINATIONS,
  TRIPLE_ROOM_COMBINATIONS,
} from '@/constants/HotelRoomTypes';
import { Room } from './findAllRoomCombinations';
import { Translator } from '@/types/translator.type';

type RoomTypeCode = 'individual' | 'double' | 'triple' | 'quadruple';

const getRoomTypeCode = (room: Room): RoomTypeCode => {
  if (
    (INDIVIDUAL_ROOM_COMBINATIONS as Room[]).some(
      (r) => r.toString() === room.toString(),
    )
  ) {
    return 'individual';
  }
  if (
    (DOUBLE_ROOM_COMBINATIONS as Room[]).some(
      (r) => r.toString() === room.toString(),
    )
  ) {
    return 'double';
  }
  if (
    (TRIPLE_ROOM_COMBINATIONS as Room[]).some(
      (r) => r.toString() === room.toString(),
    )
  ) {
    return 'triple';
  }
  return 'quadruple';
};

const getOccupantsLabel = (room: Room, t: Translator): string => {
  const [adults, children, adolescents] = room;
  const parts: string[] = [];
  if (adults > 0)
    parts.push(
      t(`hotels.person.${adults > 1 ? 'adult_plural' : 'adult'}`, {
        count: adults,
      }),
    );
  if (children > 0)
    parts.push(
      t(`hotels.person.${children > 1 ? 'child_plural' : 'child'}`, {
        count: children,
      }),
    );
  if (adolescents > 0)
    parts.push(
      t(
        `hotels.person.${adolescents > 1 ? 'adolescent_plural' : 'adolescent'}`,
        { count: adolescents },
      ),
    );
  return parts.join(' + ');
};

export const describeRoomCombination = (
  roomCombination: Room[],
  t: Translator,
): string => {
  type RoomKey = string; // using tuple string as stable key
  const roomMap: Record<
    RoomKey,
    { count: number; typeCode: RoomTypeCode; occupantsLabel: string }
  > = {};

  for (const room of roomCombination) {
    const key = room.toString();
    if (!roomMap[key]) {
      roomMap[key] = {
        count: 0,
        typeCode: getRoomTypeCode(room),
        occupantsLabel: getOccupantsLabel(room, t),
      };
    }
    roomMap[key].count += 1;
  }

  const typeOrder: Record<RoomTypeCode, number> = {
    individual: 0,
    double: 1,
    triple: 2,
    quadruple: 3,
  };

  const parts = Object.entries(roomMap)
    .toSorted(([, a], [, b]) => typeOrder[a.typeCode] - typeOrder[b.typeCode])
    .map(([, { count, typeCode, occupantsLabel }]) => {
      const typeLabel = t(
        `hotels.roomDescriptions.${typeCode}${count > 1 ? '_plural' : ''}`,
        { count },
      );
      return `${count} × ${typeLabel} (${occupantsLabel})`;
    });
  return parts.join(' + ');
};
